import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

interface CounterItem {
  value: number;
  suffix: string;
  labelKey: string;
  icon: string;
}

const counters: CounterItem[] = [
  { value: 6, suffix: '', labelKey: 'numbers.courts', icon: 'ri-flag-line' },
  { value: 14, suffix: 'h', labelKey: 'numbers.hours', icon: 'ri-time-line' },
  { value: 2, suffix: '', labelKey: 'numbers.itf', icon: 'ri-trophy-line' },
  { value: 10, suffix: '', labelKey: 'numbers.coaches', icon: 'ri-user-star-line' },
  { value: 20, suffix: '', labelKey: 'numbers.sparring', icon: 'ri-team-line' },
  { value: 40, suffix: '', labelKey: 'numbers.school', icon: 'ri-heart-pulse-line' },
];

function AnimatedCounter({ target, suffix }: { target: number; suffix: string }) {
  const ref = useRef<HTMLSpanElement>(null);
  const hasAnimated = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || hasAnimated.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated.current) {
          hasAnimated.current = true;
          // Animate from 0 to target using CSS counter
          const duration = 2000;
          const start = performance.now();

          function tick(now: number) {
            const elapsed = now - start;
            const progress = Math.min(elapsed / duration, 1);
            // Ease-out curve
            const eased = 1 - Math.pow(1 - progress, 3);
            const current = Math.round(eased * target);
            if (el) el.textContent = `${current}${suffix}`;
            if (progress < 1) {
              requestAnimationFrame(tick);
            }
          }

          requestAnimationFrame(tick);
          observer.disconnect();
        }
      },
      { threshold: 0.3 }
    );

    observer.observe(el);

    // Fallback: animate after 2s if observer hasn't fired
    const fallback = setTimeout(() => {
      if (!hasAnimated.current) {
        hasAnimated.current = true;
        const duration = 2000;
        const start = performance.now();

        function tick(now: number) {
          const elapsed = now - start;
          const progress = Math.min(elapsed / duration, 1);
          const eased = 1 - Math.pow(1 - progress, 3);
          const current = Math.round(eased * target);
          if (el) el.textContent = `${current}${suffix}`;
          if (progress < 1) {
            requestAnimationFrame(tick);
          }
        }

        requestAnimationFrame(tick);
        observer.disconnect();
      }
    }, 2000);

    return () => {
      observer.disconnect();
      clearTimeout(fallback);
    };
  }, [target, suffix]);

  return (
    <span ref={ref} className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-primary-600">
      {target}{suffix}
    </span>
  );
}

export default function KeyNumbers() {
  const { t } = useTranslation();

  return (
    <section className="relative py-16 md:py-20 bg-background-50">
      <div className="w-full px-4 md:px-6 lg:px-10">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-6 md:gap-8">
            {counters.map((item) => (
              <div
                key={item.labelKey}
                className="flex flex-col items-center text-center p-6 rounded-2xl bg-background-100/70 border border-background-200/60 transition-all duration-300 hover:border-accent-300/40 hover:bg-background-100"
              >
                <div className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mb-4">
                  <i className={`${item.icon} text-lg md:text-xl`}></i>
                </div>
                <AnimatedCounter target={item.value} suffix={item.suffix} />
                <p className="text-xs md:text-sm text-foreground-600 mt-2 leading-tight max-w-[140px]">
                  {t(item.labelKey)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
